import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  XCircleFill,
  ExclamationTriangleFill,
  CheckCircleFill,
  InfoCircleFill,
  Clipboard,
  ClipboardCheck,
} from "react-bootstrap-icons";

const SEVERITY_ICON = {
  error: XCircleFill,
  warning: ExclamationTriangleFill,
  success: CheckCircleFill,
  info: InfoCircleFill,
  neutral: null,
};

const SEVERITY_VARIANT = {
  error: "danger",
  warning: "warning",
  success: "success",
  info: "primary",
  neutral: "primary",
};

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  color: ${(p) => p.theme.color[p.$severity] || p.theme.color.primary};
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  font-size: ${(p) => p.theme.font.sizeLg};
  line-height: 1.2;
`;

const SeverityStripe = styled.div`
  height: 4px;
  background: ${(p) => p.theme.color[p.$severity] || p.theme.color.primary};
  border-top-left-radius: ${(p) => p.theme.radius.md};
  border-top-right-radius: ${(p) => p.theme.radius.md};
`;

const Body = styled.div`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  line-height: 1.5;
`;

const Details = styled.details`
  margin-top: ${(p) => p.theme.spacing.md};
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.sm};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};

  & > summary {
    cursor: pointer;
    user-select: none;
    color: ${(p) => p.theme.color.textMuted};
    font-weight: ${(p) => p.theme.font.weightMedium};
  }
`;

const TechPre = styled.pre`
  margin: ${(p) => p.theme.spacing.sm} 0 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: ${(p) => p.theme.color.text};
  background: ${(p) => p.theme.color.surface};
  padding: ${(p) => p.theme.spacing.sm};
  border-radius: ${(p) => p.theme.radius.sm};
  border: 1px solid ${(p) => p.theme.color.border};
  max-height: 220px;
  overflow: auto;
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.xs};
  background: transparent;
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.sm};
  padding: 4px 10px;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
  cursor: pointer;
  margin-top: ${(p) => p.theme.spacing.sm};

  &:hover {
    background: ${(p) => p.theme.color.surface};
  }
`;

const stringifyDetail = (detail) =>
  typeof detail === "string" ? detail : JSON.stringify(detail, null, 2);

const FeedbackModal = ({
  show,
  onHide,
  severity = "info",
  title,
  message,
  technicalDetail,
  primaryAction,
  secondaryAction,
  size,
  ...rest
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const Icon =
    severity in SEVERITY_ICON ? SEVERITY_ICON[severity] : InfoCircleFill;

  const handleCopy = async () => {
    if (!technicalDetail) return;
    try {
      await navigator.clipboard.writeText(stringifyDetail(technicalDetail));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard API can fail in non-secure contexts; degrade silently
    }
  };

  const resolvedTitle = title || t(`feedback.${severity}.title`);
  const hasActions = primaryAction || secondaryAction;

  return (
    <Modal show={show} onHide={onHide} size={size} centered {...rest}>
      <SeverityStripe $severity={severity} />
      <Modal.Header closeButton>
        <TitleBar $severity={severity}>
          {Icon ? <Icon size={20} aria-hidden /> : null}
          <span>{resolvedTitle}</span>
        </TitleBar>
      </Modal.Header>
      <Modal.Body>
        {message ? <Body>{message}</Body> : null}
        {technicalDetail ? (
          <Details>
            <summary>{t("feedback.common.technicalDetails")}</summary>
            <TechPre>{stringifyDetail(technicalDetail)}</TechPre>
            <CopyBtn type="button" onClick={handleCopy}>
              {copied ? <ClipboardCheck size={14} /> : <Clipboard size={14} />}
              {copied ? t("feedback.common.copied") : t("feedback.common.copy")}
            </CopyBtn>
          </Details>
        ) : null}
      </Modal.Body>
      {hasActions ? (
        <Modal.Footer>
          {secondaryAction ? (
            <Button
              variant={secondaryAction.variant || "light"}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
          {primaryAction ? (
            <Button
              variant={
                primaryAction.variant || SEVERITY_VARIANT[severity] || "primary"
              }
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          ) : null}
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

export default FeedbackModal;
